import React from "react";
import { useInView } from "react-intersection-observer";

import useLazyLoadCheck from "@brexhq/next/src/hooks/useLazyLoadCheck";
import { logMiscError } from "@brexhq/next/src/telemetry/logging";
import { Winda } from "@brexhq/next/src/utils/Winda";

import { StyledVideoProps } from "./types";
import { Video } from "./video";

/**
 * Videos that autoplay in chromatic are a waste of bandwidth that we pay for
 */
const shouldAutoPlay = (autoPlay = true) => {
  if (!autoPlay) return false;
  if (Winda.isPresent) return !window.location.href.includes("chromatic");
  return true;
};

/**
 *
 * @param shouldLazyLoad whether the referenced video has been set to lazy load
 * @param videoRef reference to the video that you want to lazy load. Should be the same reference you use for <Video>
 * @returns
 */

type LazyLoad = {
  shouldLazyLoad: boolean;
  videoRef?: React.MutableRefObject<HTMLVideoElement | undefined | null>;
};
export const lazyLoadVideo = ({
  shouldLazyLoad,
  videoRef,
}: LazyLoad): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    if (shouldLazyLoad && videoRef?.current) {
      for (const childIndex in videoRef.current.children) {
        const child = videoRef.current.children[childIndex];
        if (child instanceof HTMLSourceElement) {
          child.src = child.dataset.src ?? "";
        }
      }
      videoRef.current.oncanplay = () => {
        resolve(true);
      };
      videoRef.current.onerror = (err) => {
        reject(err);
      };
      videoRef.current.load();
    }
    resolve(true);
  });
};

/**
 * Video that can autoplay when visible in the viewport
 */
const IntersectionVideo = React.forwardRef<HTMLVideoElement, StyledVideoProps>(
  (props, forwardedRef) => {
    const { autoPlay, src, onProgressUpdate, onVideoEnded } = props;
    const videoRef = React.useRef<HTMLVideoElement>();

    const [inViewRef, inView] = useInView();

    // This intersection observer is just used to preload the video with the root margin. If we used the rootmargin on the other element it would also start playing before it was in view so we invoke a second one
    const [loaderRef, loaderInView] = useInView({
      threshold: 0,
      rootMargin: "0px 0px 350px 0px",
      triggerOnce: true,
    });

    const videoPromiseRef = React.useRef(Promise.resolve());
    const shouldLazyLoad = useLazyLoadCheck();
    const [isVideoLoaded, setIsVideoLoaded] = React.useState(!shouldLazyLoad);
    const [loadedVideoSrc, setLoadedVideoSrc] = React.useState(src);

    const setRefs = React.useCallback(
      (node) => {
        // Ref's from useRef needs to have the node assigned to `current`
        videoRef.current = node;
        // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
        inViewRef(node);

        // We have multple refs - so we can have an offset which we use to load the video file. When the video enters the viewport we start the autoplay (above ref)

        loaderRef(node);

        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (typeof forwardedRef === "object" && forwardedRef !== null) {
          forwardedRef.current = node;
        }
      },
      [inViewRef, loaderRef, forwardedRef],
    );

    const handleTimeUpdate = React.useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        if (!e?.currentTarget) return;

        const progress =
          Math.round(
            (e.currentTarget.currentTime / e.currentTarget.duration) * 100,
          ) / 100;

        if (Number(progress)) {
          onProgressUpdate?.({ progress });
        }
      },
      [onProgressUpdate],
    );

    // This UU just preloads the video file
    React.useEffect(() => {
      // We set the isLoaded state so we don't unnecessarily call the lazyLoadVideo function again in the 2nd useEffect
      const handleLazyLoad = async () => {
        // Early return if we don't need to lazy load ie the element is high up in the viewport
        if (!shouldLazyLoad) return;
        const hasLoaded = await lazyLoadVideo({
          shouldLazyLoad: loaderInView,
          videoRef,
        });
        if (hasLoaded) {
          setIsVideoLoaded(true);
        }
      };
      handleLazyLoad();
    }, [loaderInView, videoRef, shouldLazyLoad]);

    // This UE is used for toggling play/pause state based on the inView state
    React.useEffect(() => {
      const isPlaying = !videoRef.current?.paused;
      if (
        inView &&
        videoRef.current &&
        shouldAutoPlay(autoPlay) &&
        !isPlaying
      ) {
        videoPromiseRef.current = videoPromiseRef.current
          .then(() => {
            if (!shouldLazyLoad) return true;
            // we could maybe remove this second invocation of lazyLoadVideo and just use the one in the useEffect above, but its here incase the other observer fails to run for some reason
            return isVideoLoaded || lazyLoadVideo({ shouldLazyLoad, videoRef });
          })
          .then((hasLoaded) => {
            if (hasLoaded) {
              setIsVideoLoaded(true);
              return videoRef.current?.play().catch((e) => {
                console.log("Error playing video:", e);
              });
            }
            return undefined;
          });
      }

      if (!inView && videoRef.current && isPlaying) {
        videoPromiseRef.current = videoPromiseRef.current
          .then(() => {
            return videoRef.current?.pause();
          })
          .catch((e) => {
            console.log("Error playing video:", e);
          });
      }

      return () => {
        if (isPlaying) {
          videoPromiseRef.current = videoPromiseRef.current
            .then(() => {
              videoRef.current?.pause();
            })
            .catch((e) => {
              logMiscError(e);
            });
        }
      };
    }, [inView, videoRef, autoPlay, shouldLazyLoad, isVideoLoaded]);

    // Reload the video if it has already been loaded but the src has changed
    React.useEffect(() => {
      if (isVideoLoaded && loadedVideoSrc !== src && videoRef.current) {
        setLoadedVideoSrc(src);
        videoRef.current.oncanplay = () => {
          videoRef.current?.play();
        };
        videoRef.current?.load();
      }
    }, [src, isVideoLoaded, loadedVideoSrc, videoRef]);

    return (
      <Video
        onEnded={() => onVideoEnded?.()}
        ref={setRefs}
        onTimeUpdateCapture={handleTimeUpdate}
        {...props}
        shouldLazyLoad={shouldLazyLoad}
      />
    );
  },
);

IntersectionVideo.displayName = "IntersectionVideo";

export { IntersectionVideo };
